<template>
    <aside
        :class="[
            useSideBarStore.isOpen ? 'bg-nav-bg items-start' : 'bg-nav-bg items-center',
            'relative border-r border-r-nav-border shrink-0 hidden md:flex flex-col overflow-hidden h-screen max-h-screen transition-colors duration-300 ease-in-out py-5',
        ]">
        <div class="w-full" :class="[sidebarPadding, { 'flex justify-center': !useSideBarStore.isOpen }]">
            <BaseTooltip side="right">
                <template #trigger>
                    <button
                        data-testid="sidebar-button"
                        type="button"
                        :aria-expanded="useSideBarStore.isOpen"
                        :aria-label="useSideBarStore.isOpen ? 'Collapse sidebar' : 'Expand sidebar'"
                        class="text-nav-item-text hover:text-nav-item-text-hover transition-colors group"
                        @click="handleSidebarToggle">
                        <!-- Default icons (shown when not hovering) -->
                        <i class="bi bi-layout-sidebar text-2xl group-hover:hidden" aria-hidden="true"></i>

                        <!-- Hover icons (shown only when hovering) -->
                        <i v-if="useSideBarStore.isOpen" class="bi bi-arrow-bar-left text-2xl hidden group-hover:block" aria-hidden="true"></i>
                        <i v-else class="bi bi-arrow-bar-right text-2xl hidden group-hover:block" aria-hidden="true"></i>
                    </button>
                </template>
                <template #content>{{ useSideBarStore.isOpen ? "Collapse sidebar" : "Expand sidebar" }}</template>
            </BaseTooltip>
        </div>
        <div v-if="!userDetails?.is_anonymous" class="mt-4 mb-10 grow flex overflow-y-auto w-full h-full" :class="pane ? 'max-w-87.5' : 'max-w-nav-max-width'">
            <div v-if="pane" class="overflow-hidden flex flex-col grow" :class="[sidebarPadding]">
                <div class="border-b border-b-nav-pane-divider shrink-0 pb-2">
                    <button type="button" title="Return" class="body hover:text-nav-menu-text-hover" @click="clearPane">
                        <span class="bi bi-chevron-left mr-1" aria-hidden="true"></span>
                        <span v-text="pane.title"></span>
                    </button>
                </div>
                <div class="grow overflow-hidden flex flex-col">
                    <div v-if="loading" class="flex p-4 w-full justify-center">
                        <div class="w-8 h-8 text-nav-loading">
                            <LoadingSpinner />
                        </div>
                    </div>
                    <div v-else-if="error">
                        <p class="p-4 body-small">Failed to load information. Please try again later.</p>
                    </div>
                    <div v-else-if="paneComponent" class="flex flex-col grow overflow-y-auto">
                        <component :is="paneComponent.component" v-bind="paneComponent.props" />
                    </div>
                </div>
            </div>
            <!-- Use v-show instead of v-if to keep MainMenu's internal state -->
            <MainMenu
                v-show="!pane"
                :show-labels="useSideBarStore.isOpen"
                :show-section-titles="useSideBarStore.isOpen"
                :enabled-panes="mainMenuPanes"
                :panes-with-notifications="panesWithNotifications"
                :pane-notification-counts="paneNotificationCounts"
                :class="[sidebarPadding, { 'items-start': useSideBarStore.isOpen, 'items-center': !useSideBarStore.isOpen }]"
                :loading-work-panes="!panesReady"
                @open-pane="openPane" />
        </div>

        <div v-if="useSideBarStore.isOpen" class="mb-6 w-full" :class="[sidebarPadding]">
            <OrganizationLogo image-class="h-5 w-auto object-contain max-w-[150px] mb-4" />
            <p v-if="useSideBarStore.isOpen && userDetails?.can_receive_emails" class="body-small text-nav-text-muted break-all max-w-nav-max-width fs-mask">
                {{ userDetails?.email }}
            </p>
            <hr class="h-0.5 bg-nav-divider mt-6" />
        </div>

        <div class="hidden md:flex flex-col gap-5">
            <div v-if="userDetails?.is_customer_demo_accounts_enabled" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseLink
                            data-testid="nav-demo-accounts"
                            href="/demo-accounts/"
                            aria-label="Demo Accounts"
                            class="body-small text-[#3B3B3B] hover:text-[#3B3B3B]/80 transition-colors flex flex-row items-center leading-none!">
                            <i class="bi bi-person-badge nav-icon leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 flex flex-row items-center text-left">Demo Accounts</span>
                        </BaseLink>
                    </template>
                    <template #content>Demo Accounts</template>
                </BaseTooltip>
            </div>

            <div v-if="userDetails?.can_see_client_analytics" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseLink
                            data-testid="nav-analytics"
                            href="/client-analytics/"
                            aria-label="Client Analytics"
                            class="body-small text-nav-item-text hover:text-nav-item-text-hover transition-colors flex flex-row items-center leading-none!">
                            <i class="bi bi-graph-up nav-icon leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 flex flex-row items-center text-left">Analytics</span>
                        </BaseLink>
                    </template>
                    <template #content>Analytics</template>
                </BaseTooltip>
            </div>

            <div v-if="orgDetails?.team_tools_link_enabled" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <a
                            data-testid="nav-team-tools"
                            :href="alignHost"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Valence Team Tools"
                            class="body-small text-nav-item-text hover:text-nav-item-text-hover transition-colors flex flex-row items-center leading-none"
                            @click="logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: 'team-tools', title: 'Valence Team Tools' })">
                            <i class="bi bi-box-arrow-up-right nav-icon leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 flex items-center text-left">Valence Team Tools</span>
                        </a>
                    </template>
                    <template #content>Valence Team Tools</template>
                </BaseTooltip>
            </div>

            <!-- Temporary WorkIQ Demo -->
            <div v-if="userDetails?.has_workiq_connected" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <BaseLink
                            data-testid="nav-workiq-insights"
                            href="/workiq/insights/"
                            aria-label="Work IQ (Demo)"
                            class="body-small text-nav-item-text hover:text-nav-item-text-hover transition-colors flex flex-row items-center leading-none!">
                            <i class="bi bi-stars nav-icon leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 flex flex-row items-center text-left">Work IQ (Demo)</span>
                        </BaseLink>
                    </template>
                    <template #content>Work IQ (Demo)</template>
                </BaseTooltip>
            </div>

            <div v-if="!userDetails?.is_anonymous" :class="[sidebarPadding]" class="w-full">
                <BaseTooltip :disabled="useSideBarStore.isOpen" side="right">
                    <template #trigger>
                        <button
                            data-testid="nav-privacy"
                            type="button"
                            :aria-label="privacyLabel"
                            class="body-small text-nav-item-text hover:text-nav-item-text-hover transition-colors flex flex-row items-center leading-none!"
                            @click="openPrivacyDialog">
                            <i class="bi bi-shield-lock nav-icon leading-none" aria-hidden="true" />
                            <span v-if="useSideBarStore.isOpen" class="ml-3 flex flex-row items-center text-left">{{ privacyLabel }}</span>
                        </button>
                    </template>
                    <template #content>{{ privacyLabel }}</template>
                </BaseTooltip>
                <PrivacyNoticeDialog v-model:open="privacyDialogOpen" />
            </div>

            <div :class="[sidebarPadding]" class="w-full">
                <SettingsMenu :show-label="useSideBarStore.isOpen" />
            </div>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { OrganizationDetails, UserDetails } from "~vue/appTypes";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { useSideBarStore } from "~vue/stores/sideBarStore";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, inject, onMounted, type Ref, ref } from "vue";

import OrganizationLogo from "../OrganizationLogo.vue";
import MainMenu from "./MainMenu.vue";
import { ALL_PANES, MainMenuPane, type PaneNotificationCounts, validateDisabledPanes } from "./menuPanes";
import SettingsMenu from "./SettingsMenu.vue";

interface MenuPane {
    id: string;
    title: string;
    component?: Component;
}

interface PaneComponentSlot {
    component: Component;
    props: Record<string, unknown>;
}

interface Props {
    panesWithNotifications?: MainMenuPane[];
    paneNotificationCounts?: PaneNotificationCounts;
}

withDefaults(defineProps<Props>(), {
    panesWithNotifications: () => [],
    paneNotificationCounts: () => ({}),
});

const disabledPanes = ref<MainMenuPane[]>([
    MainMenuPane.GUIDED_SETUP,
    MainMenuPane.COLLABORATION_PROFILE,
    MainMenuPane.CONNECTIONS,
    MainMenuPane.JOURNEYS,
    MainMenuPane.DEVELOPMENT_JOURNEYS,
    MainMenuPane.MY_WORK,
    MainMenuPane.PERFORMANCE_MANAGEMENT,
    MainMenuPane.COMPETENCY_ASSESSMENT,
    MainMenuPane.SCHNEIDER_ASSESSMENT,
    MainMenuPane.REFLECTIONS,
]);
const pane = ref<MenuPane | null>(null);
const paneComponent = ref<PaneComponentSlot | null>(null);
const error = ref<Record<string, unknown> | null>(null);

const navigationUrl = ref("/navigation/");
const {
    data: navigationData,
    error: navigationError,
    isFetching: loading,
    execute: fetchNavigation,
} = useFetch(navigationUrl, { immediate: false }).get().json<Record<string, unknown>>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const orgDetails = inject<Ref<OrganizationDetails | null>>("organization_details");
const alignHost = inject<Ref<string>>("align_host");

// Apply org preference before first paint to avoid open→closed flicker
if (orgDetails?.value?.collapse_navbar_by_default) {
    useSideBarStore.isOpen = false;
} else {
    useSideBarStore.isOpen = true;
}

const panesReady = ref(!useSideBarStore.isOpen);

const mainMenuPanes = computed(() => ALL_PANES.filter((p) => !disabledPanes.value.includes(p)));

const sidebarPadding = "px-5";

const privacyDialogOpen = ref(false);
const privacyLabel = computed(() => orgDetails?.value?.privacy_link_label || "Privacy");

function openPrivacyDialog() {
    logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: "privacy", title: privacyLabel.value });
    privacyDialogOpen.value = true;
}

function clearPane() {
    pane.value = null;
}

function handleSidebarToggle() {
    if (pane.value) {
        clearPane();
    }

    useSideBarStore.toggle();
}

async function openPane(p: MenuPane) {
    pane.value = p;
    if (!useSideBarStore.isOpen) {
        useSideBarStore.toggle();
    }

    error.value = null;
    paneComponent.value = null;
    navigationUrl.value = `/navigation/?name=${p.id}`;
    await fetchNavigation();

    if (navigationError.value) {
        error.value = navigationData.value ?? (navigationError.value as Record<string, unknown>);
        return;
    }

    if (p.component && navigationData.value) {
        paneComponent.value = { component: p.component, props: navigationData.value };
    }
}

onMounted(async () => {
    disabledPanes.value = await validateDisabledPanes(disabledPanes.value);
    setTimeout(() => {
        panesReady.value = true;
    }, 300);
});
</script>
